import { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { LOGO_URL } from "../lib/brand";
import { Lock, ArrowRight, AlertCircle, Users, ShieldCheck, KeyRound } from "lucide-react";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const [token, setToken] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("password"); // password | totp | totp_setup
  const [setup, setSetup] = useState(null); // {qr, secret, otpauth_uri}
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const submitPassword = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { token });
      if (data.step === "totp_setup") {
        setSetup({ qr: data.qr, secret: data.secret });
        setStep("totp_setup");
      } else {
        setStep("totp");
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Ungültiges Passwort.");
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login/verify`, { token, code }, { withCredentials: true });
      // Session-Token liegt jetzt im httpOnly-Cookie — localStorage hält nur
      // einen nicht-sensitiven "eingeloggt"-Marker (XSS-Schutz).
      localStorage.setItem("alphalane_admin_token", "1");
      localStorage.removeItem("alphalane_partner_token");
      localStorage.removeItem("alphalane_partner_meta");
      navigate("/admin");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Ungültiger 2FA-Code.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--al-bg)] flex items-center justify-center p-5 relative overflow-hidden">
      <div className="al-blob" style={{ top: "-150px", right: "-100px", width: 480, height: 480, background: "radial-gradient(closest-side, rgba(168,85,247,0.4), transparent)" }} />
      <div className="al-blob" style={{ bottom: "-150px", left: "-100px", width: 480, height: 480, background: "radial-gradient(closest-side, rgba(74,143,231,0.4), transparent)" }} />

      <div className="relative al-card w-full max-w-md p-8 sm:p-10">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Alphalane" className="h-10 w-10 object-contain" />
          <div className="font-display">
            <div className="font-semibold text-base"><span className="al-grad-text">ALPHALANE</span></div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Admin-Login</div>
          </div>
        </div>

        {step === "password" && (
          <>
            <h1 className="mt-7 font-display font-bold text-3xl leading-tight">
              <span className="al-grad-text">Anmeldung.</span>
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Gib das Admin-Passwort ein — danach folgt die 2FA-Bestätigung.
            </p>
            <form onSubmit={submitPassword} className="mt-6 space-y-4" data-testid="admin-login-form">
              <label className="block">
                <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-semibold text-slate-500 mb-1.5">
                  <Lock className="h-3.5 w-3.5" /> Passwort
                </span>
                <input
                  data-testid="admin-login-token"
                  type="password" required
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  autoFocus
                  className="al-input"
                  placeholder="••••••••••••"
                />
              </label>
              {err && (
                <div className="flex items-center gap-2 text-sm text-rose-600" data-testid="admin-login-error">
                  <AlertCircle className="h-4 w-4" /> {err}
                </div>
              )}
              <button type="submit" data-testid="admin-login-submit" disabled={loading}
                className="al-cta w-full justify-center disabled:opacity-60">
                {loading ? "Prüfe…" : "Weiter"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </>
        )}

        {step === "totp_setup" && (
          <>
            <h1 className="mt-7 font-display font-bold text-2xl leading-tight flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-emerald-500" />
              <span className="al-grad-text">2FA einrichten.</span>
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Scanne den QR-Code mit deiner Authenticator-App (z.B. Google Authenticator)
              und gib danach den 6-stelligen Code ein. Das ist nur einmal nötig.
            </p>
            {setup?.qr && (
              <div className="mt-5 flex flex-col items-center">
                <img src={setup.qr} alt="TOTP QR-Code" className="h-44 w-44 rounded-xl border border-slate-200" data-testid="admin-totp-qr" />
                <div className="mt-2 text-[11px] text-slate-400 font-mono break-all text-center">
                  Manuell: {setup.secret}
                </div>
              </div>
            )}
            <form onSubmit={submitCode} className="mt-5 space-y-4" data-testid="admin-totp-setup-form">
              <input
                data-testid="admin-totp-code"
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                autoFocus
                className="al-input text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="000000"
              />
              {err && (
                <div className="flex items-center gap-2 text-sm text-rose-600" data-testid="admin-login-error">
                  <AlertCircle className="h-4 w-4" /> {err}
                </div>
              )}
              <button type="submit" data-testid="admin-totp-submit" disabled={loading || code.length !== 6}
                className="al-cta w-full justify-center disabled:opacity-60">
                {loading ? "Prüfe…" : "2FA aktivieren & einloggen"} <ArrowRight className="h-4 w-4" />
              </button>
            </form>
          </>
        )}

        {step === "totp" && (
          <>
            <h1 className="mt-7 font-display font-bold text-2xl leading-tight flex items-center gap-2">
              <KeyRound className="h-6 w-6 text-[var(--al-purple)]" />
              <span className="al-grad-text">2FA-Code.</span>
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Gib den 6-stelligen Code aus deiner Authenticator-App ein.
            </p>
            <form onSubmit={submitCode} className="mt-6 space-y-4" data-testid="admin-totp-form">
              <input
                data-testid="admin-totp-code"
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                autoFocus
                className="al-input text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="000000"
              />
              {err && (
                <div className="flex items-center gap-2 text-sm text-rose-600" data-testid="admin-login-error">
                  <AlertCircle className="h-4 w-4" /> {err}
                </div>
              )}
              <button type="submit" data-testid="admin-totp-submit" disabled={loading || code.length !== 6}
                className="al-cta w-full justify-center disabled:opacity-60">
                {loading ? "Prüfe…" : "Einloggen"} <ArrowRight className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => { setStep("password"); setCode(""); setErr(""); }}
                className="w-full text-xs text-slate-400 hover:text-slate-600">
                Zurück zur Passwort-Eingabe
              </button>
            </form>
          </>
        )}

        <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
          <Link to="/" className="hover:text-slate-600">← Zur Website</Link>
          <Link to="/partner/login" className="inline-flex items-center gap-1 hover:text-slate-600">
            <Users className="h-3.5 w-3.5" /> Partner-Login
          </Link>
        </div>
      </div>
    </div>
  );
}
